import React, { type ForwardedRef, forwardRef, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import makeStyles from '@mui/styles/makeStyles'
import { pluck } from 'ramda'
import type { DocumentFile } from '@pbt/pbt-ui-components'

import { AttachmentMenu } from '~/components/dashboard/communications/conversations-chat/AttachmentMenu'
import useAttachmentHistory from '~/components/dashboard/communications/conversations-chat/useAttachmentHistory'
import DialogNames from '~/constants/DialogNames'
import FeatureToggle from '~/constants/featureToggle'
import {
  attachDocumentsToMessage,
  clearFileToSend,
} from '~/store/actions/conversationMessages'
import { getFeatureToggle } from '~/store/reducers/constants'
import { getAttachmentsToSend } from '~/store/reducers/conversationMessages'
import useDialog from '~/utils/useDialog'

import type { BaseTemplateInputHandle } from './BaseTemplateInput/BaseTemplateInput'
import WrappedBaseTemplateInput, {
  type WrappedBaseTemplateInputProps,
} from './WrappedBaseTemplateInput'

const useStyles = makeStyles(
  (theme) => ({
    title: {
      marginBottom: theme.spacing(1),
    },
    titleContainer: {
      margin: 0,
    },
  }),
  { name: 'CommunicationTemplateInput' },
)

export interface CommunicationTemplateInputProps
  extends Omit<WrappedBaseTemplateInputProps, 'filter'> {
  attachmentMenuPlacement?: 'top' | 'right' | 'bottom' | 'left'
}

const CommunicationTemplateInput = forwardRef(
  function CommunicationTemplateInput(
    {
      showAttachment = false,
      hideEmail,
      clientId,
      patientId,
      eventId,
      attachmentMenuPlacement,
      ...rest
    }: CommunicationTemplateInputProps,
    ref: ForwardedRef<BaseTemplateInputHandle>,
  ) {
    const classes = useStyles()
    const dispatch = useDispatch()
    const attachmentsToSend = useSelector(getAttachmentsToSend)
    const isCommunicationsRightRailEnabled = useSelector(
      getFeatureToggle(FeatureToggle.ENABLE_COMMUNICATIONS_RIGHT_RAIL),
    )
    const {
      attachments: attachmentHistory,
      isFetching: isFetchingAttachments,
    } = useAttachmentHistory(
      isCommunicationsRightRailEnabled ? patientId : undefined,
    )
    const { t } = useTranslation(['Common'])
    const attachmentButtonRef = useRef<HTMLButtonElement>(null)
    const [selectedAttachmentIds, setSelectedAttachmentIds] = useState<
      string[]
    >([])

    const [openAttachmentDialog] = useDialog(
      DialogNames.CONVERSATION_MULTI_ATTACHMENT_DIALOG,
    )

    const onDeleteFile = (file: DocumentFile['file']) => {
      dispatch(clearFileToSend(file.id as string))
    }

    const handleAddAttachment = () => {
      openAttachmentDialog({ clientId, patientId, eventId, ...rest })
    }

    const handleToggleAttachment = (documentId: string, nextValue: boolean) => {
      setSelectedAttachmentIds((prev) => {
        if (nextValue) {
          return prev.includes(documentId) ? prev : [...prev, documentId]
        }

        return prev.filter((id) => id !== documentId)
      })
    }

    const shouldShowAttachmentMenu =
      isCommunicationsRightRailEnabled &&
      patientId &&
      (attachmentHistory.length > 0 || isFetchingAttachments)

    const onAttachClick = () => {
      if (!shouldShowAttachmentMenu) {
        openAttachmentDialog({ clientId, patientId, eventId, ...rest })
      }
    }

    const onAttachDocuments = (documents: { id: string }[]) => {
      dispatch(
        attachDocumentsToMessage(pluck('id', documents) as string[], {
          clientId,
          patientId,
          appointmentId: eventId,
        }),
      )
    }

    const attachmentProps = showAttachment
      ? {
          hideEmail,
          showAttachment: true,
          files: attachmentsToSend,
          onAttachClick,
          onAttachDocuments,
          onDeleteFile,
        }
      : {}

    return (
      <>
        <WrappedBaseTemplateInput
          attachmentButtonRef={attachmentButtonRef}
          classes={{
            title: classes.title,
            titleContainer: classes.titleContainer,
          }}
          clientId={clientId}
          eventId={eventId}
          filter={['Communication template']}
          label={t('Common:COMMUNICATION_TEMPLATE')}
          patientId={patientId}
          ref={ref}
          {...rest}
          {...attachmentProps}
        />
        {shouldShowAttachmentMenu && (
          <AttachmentMenu
            anchorRef={attachmentButtonRef}
            attachments={attachmentHistory}
            isLoading={isFetchingAttachments}
            placement={attachmentMenuPlacement}
            selectedAttachmentIds={selectedAttachmentIds}
            onAddAttachment={handleAddAttachment}
            onToggleAttachment={handleToggleAttachment}
          />
        )}
      </>
    )
  },
)

export default CommunicationTemplateInput
